import { useState, useRef, useEffect } from 'react';
import {
  Bell,
  CheckSquare,
  AlertTriangle,
  MessageSquare,
  Handshake,
  Zap,
  BookOpen,
  Info,
  Headphones,
  Clock,
  Check,
} from 'lucide-react';
import {
  useNotifications,
  useUnreadCount,
  useMarkAsRead,
  useMarkAllAsRead,
  type Notification,
  type NotificationType,
} from '../../hooks/useNotifications';
import { cn } from '../../lib/cn';

interface NotificationBellProps {
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

const typeConfig: Record<NotificationType, { icon: typeof Bell; color: string }> = {
  task_assigned: { icon: CheckSquare, color: 'text-blue-500' },
  task_due: { icon: Clock, color: 'text-orange-500' },
  case_assigned: { icon: Headphones, color: 'text-purple-500' },
  case_escalated: { icon: AlertTriangle, color: 'text-red-500' },
  comment_mention: { icon: MessageSquare, color: 'text-brand-500' },
  deal_update: { icon: Handshake, color: 'text-green-500' },
  workflow_completed: { icon: Zap, color: 'text-yellow-500' },
  playbook_step: { icon: BookOpen, color: 'text-indigo-500' },
  system: { icon: Info, color: 'text-slate-500' },
};

function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

function getViewForEntityType(entityType?: string): string | null {
  if (!entityType) return null;
  const map: Record<string, string> = {
    task: 'tasks',
    case: 'cases',
    deal: 'deals',
    contact: 'contacts',
    organization: 'organizations',
    workflow: 'workflows',
    playbook: 'playbooks',
  };
  return map[entityType] ?? null;
}

export function NotificationBell({ onNavigate }: NotificationBellProps) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const { data: unreadData } = useUnreadCount();
  const { data: notificationsData, isLoading } = useNotifications({ limit: 20 });
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();

  const unreadCount = unreadData?.count ?? 0;
  const notifications = notificationsData?.notifications ?? [];

  // Close panel on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [open]);

  function handleNotificationClick(notification: Notification) {
    if (!notification.isRead) {
      markAsRead.mutate(notification.id);
    }
    const view = getViewForEntityType(notification.entityType);
    if (view) {
      onNavigate(view, notification.entityId ? { id: notification.entityId } : undefined);
    }
    setOpen(false);
  }

  function handleMarkAllRead() {
    markAllAsRead.mutate();
  }

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen(!open)}
        className="btn-ghost relative rounded-full p-2"
        title="Notifications"
      >
        <Bell className="h-4.5 w-4.5 text-slate-500" />
        {unreadCount > 0 && (
          <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-danger text-2xs font-bold text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 z-50 w-96 rounded-lg border border-surface-200 bg-white shadow-lg">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-surface-200 px-4 py-3">
            <h3 className="text-sm font-semibold text-slate-900">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-brand-600 hover:text-brand-700"
                disabled={markAllAsRead.isPending}
              >
                Mark all as read
              </button>
            )}
          </div>

          {/* Notification List */}
          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 space-y-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="h-8 w-8 animate-pulse rounded-full bg-surface-200" />
                    <div className="flex-1">
                      <div className="h-3 w-32 animate-pulse rounded bg-surface-200" />
                      <div className="mt-1 h-3 w-48 animate-pulse rounded bg-surface-100" />
                    </div>
                  </div>
                ))}
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Bell className="mx-auto mb-2 h-8 w-8 text-slate-300" />
                <p className="text-sm text-slate-400">No notifications</p>
              </div>
            ) : (
              <div className="divide-y divide-surface-100">
                {notifications.map((notification) => {
                  const config = typeConfig[notification.type] ?? typeConfig.system;
                  const Icon = config.icon;
                  return (
                    <div
                      key={notification.id}
                      className={cn(
                        'flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-surface-50 transition-colors',
                        !notification.isRead && 'bg-brand-50/30',
                      )}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div
                        className={cn(
                          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-100',
                          config.color,
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <p
                            className={cn(
                              'text-sm',
                              notification.isRead ? 'text-slate-600' : 'text-slate-900 font-medium',
                            )}
                          >
                            {notification.title}
                          </p>
                          {!notification.isRead && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRead.mutate(notification.id);
                              }}
                              className="shrink-0 p-0.5 text-slate-400 hover:text-brand-600"
                              title="Mark as read"
                            >
                              <Check className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                        <p className="mt-0.5 text-xs text-slate-400 truncate">
                          {notification.message}
                        </p>
                        <span className="text-xs text-slate-400">
                          {formatTimestamp(notification.createdAt)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
